import { playWheelTick, playFinalCheer } from "../sound_manager";
import { triggerHapticFeedback } from "../haptics";

const EPSILON = 0.0001;

// Short enough that consecutive ticks stay distinct at full spin speed.
const TICK_VIBRATION_MS = 8;

function getRotationDegrees(element) {
  if (!element) {
    return 0;
  }

  const style = window.getComputedStyle(element);
  const transform = style.transform || style.webkitTransform;

  if (!transform || transform === "none") {
    return 0;
  }

  let values;

  if (transform.startsWith("matrix3d")) {
    values = transform
      .slice(9, -1)
      .split(",")
      .map((v) => parseFloat(v.trim()));

    if (values.length < 16) {
      return 0;
    }

    const m13 = values[2];
    const m33 = values[10];

    const angle = Math.atan2(m13, m33);
    return (angle * 180) / Math.PI;
  }

  if (transform.startsWith("matrix")) {
    values = transform
      .slice(7, -1)
      .split(",")
      .map((v) => parseFloat(v.trim()));

    if (values.length < 4) {
      return 0;
    }

    const [a, b] = values;
    const angle = Math.atan2(b, a);
    return (angle * 180) / Math.PI;
  }

  return 0;
}

const SpinFtpWheelHook = {
  mounted() {
    this.componentSelector =
      this.el.getAttribute("phx-target") || `#${this.el.id}`;
    this.tickState = null;
    this.spinCompleteTimeout = null;

    this.el.addEventListener("click", () => {
      this.pushEventTo(this.componentSelector, "spin-wheel");
    });
  },
  updated() {
    const rawAngle = this.el.getAttribute("wheel-angle");
    const angle =
      rawAngle === null || rawAngle === "" ? null : Number(rawAngle);

    const angleChanged = angle !== null && angle !== this.angle;

    if (angle !== null && angleChanged) {
      this.angle = angle;
      const durationMs = Number(this.el.getAttribute("spin-duration-ms")) || 0;

      this.stopMonitoringSpin();

      if (durationMs > 0) {
        this.startMonitoringSpin(durationMs);
        this.scheduleSpinComplete(durationMs);
      }
    }
  },
  destroyed() {
    this.stopMonitoringSpin();
    if (this.spinCompleteTimeout) {
      clearTimeout(this.spinCompleteTimeout);
      this.spinCompleteTimeout = null;
    }
  },
  scheduleSpinComplete(durationMs) {
    if (this.spinCompleteTimeout) {
      clearTimeout(this.spinCompleteTimeout);
    }

    this.spinCompleteTimeout = window.setTimeout(() => {
      this.stopMonitoringSpin();
      playFinalCheer();
      this.pushEventTo(this.componentSelector, "spin-complete");
    }, durationMs);
  },
  startMonitoringSpin(durationMs) {
    const wheelTargetId = this.el.getAttribute("data-wheel-target");
    if (!wheelTargetId) {
      return;
    }

    const wheelElement = document.getElementById(wheelTargetId);
    if (!wheelElement) {
      return;
    }

    const sliceCount = Number(this.el.getAttribute("data-slice-count")) || 10;
    const segmentAngle = 360 / Math.max(sliceCount, 1);

    const initialAngle = getRotationDegrees(wheelElement);

    const state = {
      wheelElement,
      segmentAngle,
      accumulated: 0,
      nextBoundary: segmentAngle,
      previousAngle: initialAngle,
      startAngle: null,
      direction: null,
      rafId: null,
      stopped: false,
      tickCount: 0,
      startTime: performance.now(),
      maxDuration: durationMs + 400,
    };

    const step = (timestamp) => {
      if (state.stopped) {
        return;
      }

      const currentAngle = getRotationDegrees(state.wheelElement);

      if (
        Number.isFinite(currentAngle) &&
        Number.isFinite(state.previousAngle)
      ) {
        let delta = currentAngle - state.previousAngle;

        while (delta <= -180) {
          delta += 360;
        }

        while (delta > 180) {
          delta -= 360;
        }

        const travelled = Math.abs(delta);

        if (travelled > EPSILON) {
          if (!state.direction) {
            state.direction = delta < 0 ? -1 : 1;
            const startAngle = state.startAngle ?? currentAngle - delta;
            state.startAngle = startAngle;

            if (Number.isFinite(startAngle)) {
              const normalizedStart = ((startAngle % 360) + 360) % 360;

              let remainder = normalizedStart % state.segmentAngle;
              if (Math.abs(remainder) < EPSILON) {
                remainder = 0;
              }

              const distanceToBoundary =
                remainder === 0
                  ? state.segmentAngle
                  : state.direction < 0
                  ? remainder
                  : state.segmentAngle - remainder;

              state.nextBoundary =
                distanceToBoundary <= EPSILON
                  ? state.segmentAngle
                  : distanceToBoundary;
            }
          }

          state.accumulated += travelled;

          while (state.accumulated + EPSILON >= state.nextBoundary) {
            state.tickCount += 1;
            playWheelTick(state.tickCount);
            triggerHapticFeedback(TICK_VIBRATION_MS);
            state.nextBoundary += state.segmentAngle;
          }
        }
      }

      state.previousAngle = currentAngle;

      if (timestamp - state.startTime >= state.maxDuration) {
        this.stopMonitoringSpin();
        return;
      }

      state.rafId = requestAnimationFrame(step);
    };

    const onTransitionEnd = () => {
      window.setTimeout(() => this.stopMonitoringSpin(), 120);
    };

    state.wheelElement.addEventListener("transitionend", onTransitionEnd, {
      once: true,
    });

    state.cleanup = () => {
      state.wheelElement.removeEventListener("transitionend", onTransitionEnd);
    };

    state.rafId = requestAnimationFrame(step);
    this.tickState = state;
  },
  stopMonitoringSpin() {
    if (!this.tickState) {
      return;
    }

    this.tickState.stopped = true;

    if (this.tickState.cleanup) {
      this.tickState.cleanup();
    }

    if (this.tickState.rafId) {
      cancelAnimationFrame(this.tickState.rafId);
    }

    this.tickState = null;
  },
};

export default SpinFtpWheelHook;
